import {
  SliceCaseReducers,
  SliceSelectors,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { api } from '../../api';
import { LoggerService } from '../../shared/logger';
import { REDUCER_KEYS } from './const';
import { BhabiStorage } from '../../shared/lib';
import { Capacitor } from '@capacitor/core';

export interface User {
  id: string;
  email: string;
  name: string;
  activatedAt: {
    Time: string;
    Valid: boolean;
  };
  state: 'online' | 'offline' | 'joined' | 'rejoined' | 'left';
  coins: number;
  // photo from google or uploaded by user
  photoURL: string | null;
  firebaseUID: string | null;
  lastFreeSpin: string | null;
  lastTimeAskedForNotificationToken: string | null;
  registeredNotificationToken: boolean;
  /** 'guest' for name-only players created via /guest-session. */
  userType?: 'guest' | 'user' | 'bot';
}

export interface UserState extends User {
  isAuthenticated: boolean;
  isIdle: boolean;
  isLoading: boolean;
  isError: boolean;
  token: string | null;
  isInRoom?: boolean;
  /** Guest session existed but its token expired / was rejected. */
  guestExpired?: boolean;
  /** Last guest display name, kept for one-tap recovery after expiry. */
  guestName?: string | null;
  /** Session bootstrap could not reach the API (offline / server down). */
  bootError?: 'network' | null;
}

export const userInitialState: UserState = {
  id: '',
  email: '',
  name: '',
  activatedAt: {
    Time: '',
    Valid: false,
  },
  state: 'offline',
  firebaseUID: null,
  isAuthenticated: false,
  isIdle: true,
  isLoading: false,
  isError: false,
  token: null,
  isInRoom: false,
  photoURL: null,
  coins: 0,
  lastFreeSpin: null,
  lastTimeAskedForNotificationToken: null,
  registeredNotificationToken: false,
  guestExpired: false,
  guestName: null,
  bootError: null,
};

export const userSlice = createSlice<
  UserState,
  SliceCaseReducers<UserState>,
  string,
  SliceSelectors<UserState>
>({
  name: REDUCER_KEYS.USER,
  initialState: userInitialState,
  reducers: {
    updateUserState: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateUserCoins: (state, action) => {
      state.coins = action.payload;
    },
    logoutUser: () => ({
      ...userInitialState,
      isIdle: false,
    }),
  },
  extraReducers: builder => {
    builder
      .addCase(getUser.pending, state => {
        state.isIdle = false;
        state.isLoading = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
        };
      })
      .addCase(getUser.rejected, state => {
        state.isLoading = false;
        state.isError = true;
        state.isAuthenticated = false;
      });
  },
});

export const getUser = createAsyncThunk(
  'userSlice/getUser',
  async (token: string) => {
    try {
      const userUrl = [
        '/user',
        Capacitor.getPlatform() !== 'ios' ? 'platform=web' : '',
      ].join('?');

      const response = await api<User>({
        method: 'GET',
        url: userUrl,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      await BhabiStorage.setItem('token', token);

      return {
        ...response.data,
        isAuthenticated: true,
        isIdle: false,
        isLoading: false,
        isError: false,
        token,
      };
    } catch (error) {
      LoggerService.error('Error fetching user:', error);
      await BhabiStorage.removeItem('token');
      throw error;
    }
  }
);

export const { updateUserState, logoutUser, updateUserCoins } =
  userSlice.actions;

export const userReducer = userSlice.reducer;
